import { ICellRendererParams } from 'ag-grid-community';
import { COMMA, ENTER, SEMICOLON, SPACE } from '@angular/cdk/keycodes';
import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { InputChipsComponent } from '../../../chips/input-chips/input-chips.component';
import { AlertApiClientService } from '../../services/alert-api-client.service';

@Component({
  selector: 'app-cc-renderer',
  templateUrl: './cc-renderer.component.html',
  styleUrls: ['./cc-renderer.component.scss'],
  standalone: false,
})
export class CcRendererComponent implements OnInit, OnDestroy {
  private static readonly VISIBLE_ROWS = 3;
  private static readonly LIST_HEIGHT = 140;

  private fb = inject(FormBuilder);
  private readonly alertApiClientService = inject(AlertApiClientService);
  private hostEl = inject<ElementRef<HTMLElement>>(ElementRef);

  get maxVisibleRows(): number {
    return CcRendererComponent.VISIBLE_ROWS;
  }
  get maxListHeight(): number {
    return CcRendererComponent.LIST_HEIGHT;
  }
  @ViewChild('ccChips') private ccChips?: InputChipsComponent;
  public ccForm!: FormGroup;
  public params!: ICellRendererParams;
  public readonly pending: string[] = [];
  private lastCommitted: string[] = [];
  public separatorKeysCodes: number[] = [COMMA, SPACE, ENTER, SEMICOLON];

  public expanded = false;
  private justExpandedViaClick = false;
  private flushTimer: any;

  agInit(params: ICellRendererParams): void {
    this.params = params;
    this.pending.splice(0, this.pending.length, ...(params.value || []));
    this.lastCommitted = [...this.pending];
  }

  ngOnInit(): void {
    this.ccForm = new FormGroup({
      ccAlert: this.fb.control('', [Validators.email]),
    });
  }

  onChipsChange(updated: string[]): void {
    const normalised = updated.map((e) => e.trim()).filter(Boolean);
    this.pending.splice(0, this.pending.length, ...normalised);
    this.params.data.cc = [...this.pending];
    if (!this.expanded) {
      this.scheduleFlush();
    }
  }

  expand(): void {
    if (!this.expanded) {
      this.expanded = true;
      this.justExpandedViaClick = true;
      setTimeout(() => (this.justExpandedViaClick = false), 50);
      const chipsInstance: any = this.ccChips;
      if (chipsInstance && typeof chipsInstance.focusInput === 'function') {
        chipsInstance.focusInput();
      }
    }
  }

  collapse(): void {
    if (this.expanded) {
      this.flush();
      this.expanded = false;
    }
  }

  onFocusOut(): void {
    setTimeout(() => {
      const active = document.activeElement as HTMLElement | null;
      if (this.justExpandedViaClick) {
        return;
      }
      if (!active || !this.hostEl.nativeElement.contains(active)) {
        this.collapse();
      }
    }, 0);
  }

  private isRemoveIconClick(ev: MouseEvent): boolean {
    const target = ev.target as HTMLElement | null;
    return !!target?.closest('.mat-mdc-chip-remove');
  }

  onExpandClick(ev: MouseEvent): void {
    if (this.isRemoveIconClick(ev)) {
      return;
    }
    ev.stopPropagation();
    if (!this.expanded) {
      this.expand();
    }
  }

  onMouseDown(ev: MouseEvent): void {
    if (this.isRemoveIconClick(ev)) {
      return;
    }
    if (!this.expanded) {
      this.justExpandedViaClick = true;
      this.expand();
    }
  }

  ngOnDestroy(): void {
    this.flush();
  }

  private flush(): void {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    if (!this.arrEqual(this.pending, this.lastCommitted)) {
      const payload = { ...this.params.data, cc: this.pending };
      this.alertApiClientService.updateAlert(payload).subscribe(() => {
        this.lastCommitted = [...this.pending];
        this.params.data.cc = [...this.pending];
      });
    }
  }

  private scheduleFlush(): void {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
    }
    this.flushTimer = setTimeout(() => {
      this.flush();
    }, 250);
  }

  private arrEqual(a: string[], b: string[]): boolean {
    if (a.length !== b.length) {
      return false;
    }
    return a.every((v, i) => v === b[i]);
  }
}
